import { fromJS } from 'immutable';
import {
  reInitializeFieldValidity,
  updateFormValue,
  updateUIState,
  updateFieldOverrideState,
  updateFieldConditionality,
} from 'containers/FormBuilder/actions';
import { FIELD_TYPES, GDPR_SETTING } from 'utils/constants';
import {
  createDefaultUIStateForType,
  createDefaultInvalidFormDataForType,
  createDefaultFieldStateForType,
} from 'utils/editorDefaultStates';
import { convertDropdownValueToKey } from 'utils/idUtils';
import { buildDropdownConfig, buildMultipicklistConfig, deriveFieldTypeFromName, buildRadioButtonConfig } from './asyncutils';

export function updateFieldType(keyPath, incomingFieldName, currentFieldSlice, gdprSetting, sfData, credentialType) {
  const editorKeyPath = [...keyPath, 'editors'];
  const incomingFieldType = deriveFieldTypeFromName(incomingFieldName, sfData);
  let shouldMakeDirty = true;
  let reInitializedFieldEditorState;
  let reInitializedUIStateFieldData;

  const reInitalizedInvalidFieldData = createDefaultInvalidFormDataForType(incomingFieldType, currentFieldSlice.hidden);

  if (incomingFieldType === FIELD_TYPES.DROPDOWN) {
    [reInitializedFieldEditorState, reInitializedUIStateFieldData] = buildDropdownConfig(currentFieldSlice, sfData);
    shouldMakeDirty = false;
  } else if (incomingFieldType === FIELD_TYPES.MULTIPICKLIST) {
    [reInitializedFieldEditorState, reInitializedUIStateFieldData] = buildMultipicklistConfig(currentFieldSlice, sfData);
    shouldMakeDirty = false;
  } else if (incomingFieldType === FIELD_TYPES.RADIO_BUTTON) {
    reInitializedFieldEditorState = buildRadioButtonConfig();
    reInitializedUIStateFieldData = createDefaultUIStateForType(incomingFieldType, currentFieldSlice.hidden);
  } else {
    reInitializedFieldEditorState = createDefaultFieldStateForType(incomingFieldType, gdprSetting, credentialType);
    reInitializedUIStateFieldData = createDefaultUIStateForType(incomingFieldType, currentFieldSlice.hidden);
  }

  const actions = [
    updateFormValue([...keyPath, 'type'], incomingFieldType),
    updateFormValue(editorKeyPath, reInitializedFieldEditorState, shouldMakeDirty),
    updateUIState(editorKeyPath, reInitializedUIStateFieldData),
    reInitializeFieldValidity(editorKeyPath, fromJS(reInitalizedInvalidFieldData)),
    updateFieldOverrideState(keyPath[1], incomingFieldType, gdprSetting),
  ];

  if (incomingFieldType === FIELD_TYPES.DEGREE_OFFERING) {
    actions.push(updateFormValue([...keyPath, 'hidden'], true));
    actions.push(updateFormValue([...keyPath, 'required'], false));
  }

  if (incomingFieldType === FIELD_TYPES.PHONE && gdprSetting === GDPR_SETTING.ALWAYS_IMPLIED) {
    actions.push(updateFormValue([...keyPath, 'hidden'], false));
    actions.push(updateFormValue([...keyPath, 'required'], false));
  }

  return actions;
}

/**
 * Synchronous handler for field name updates
 * Handles UPDATE_FIELD_NAME actions and returns all required actions in order
 *
 * @param {Object} action - The UPDATE_FIELD_NAME action with keyPath and incomingFieldName
 * @param {Object} currentFieldData - Current field slice from state
 * @param {string} previousFieldName - Previous field name for conditionality update
 * @param {Object} sfData - Salesforce data for the field
 * @param {string} gdprSetting - GDPR setting from state
 * @param {string} credentialType - Credential type (DEGREE or SHORT_COURSE)
 * @returns {Array} Array of actions to dispatch in order: name update, conditionality, type updates
 */
export function updateFieldNameHandler(
  action,
  currentFieldData,
  previousFieldName,
  sfData,
  gdprSetting,
  credentialType,
) {
  const { keyPath, incomingFieldName } = action;

  // Dispatch actions in order: name update, conditionality, type updates
  const actions = [
    updateFormValue([...keyPath, 'name'], incomingFieldName),
    updateFieldConditionality(keyPath, incomingFieldName, previousFieldName),
    ...updateFieldType(keyPath, incomingFieldName, currentFieldData, gdprSetting, sfData, credentialType),
  ];

  return actions;
}

/**
 * Helper function to find differences between local dropdown options and Salesforce data
 * @param {Object} locallyStored - Locally stored dropdown options
 * @param {Array} SFStored - Salesforce picklist values
 * @returns {Object} Object with optionsToAdd and optionsOrderToAdd arrays
 */
function localToSalesForceDifference(locallyStored, SFStored) {
  const optionsToAdd = {};
  const optionsOrderToAdd = [];

  for (let i = 0; i < SFStored.length; i++) {
    const { value, label } = SFStored[i];
    const valueAsId = convertDropdownValueToKey(value);
    const presentInTaxi = locallyStored[valueAsId];

    if (!presentInTaxi) {
      optionsToAdd[valueAsId] = {
        value,
        label,
        shouldDisplay: false,
      };
      optionsOrderToAdd.push(valueAsId);
    }
  }
  return { optionsToAdd, optionsOrderToAdd };
}

/**
 * Synchronous handler for dropdown field updates
 * Handles RECEIVE_SF_FIELD_DATA and SET_FORM actions to merge Salesforce dropdown options
 * with locally stored options
 *
 * @param {Object} allFields - All fields from the form state
 * @param {Function} getSalesForceData - Function to get SF data for a field name
 * @returns {Array} Array of actions to dispatch for updating dropdown fields
 */
export function loadDropdownHandler(allFields, getSalesForceData) {
  const actions = [];

  // Get all dropdown fields
  const dropdownFields = Object.keys(allFields)
    .map((fieldId) => {
      if (allFields[fieldId].type === 'dropdown') return allFields[fieldId];
      return null;
    })
    .filter((el) => el);

  // Process each dropdown field
  for (let i = 0; i < dropdownFields.length; i++) {
    const field = dropdownFields[i];
    const sfData = getSalesForceData(field.name);
    const sfValuesToMerge = sfData?.sfPicklistValues ? sfData.sfPicklistValues : [];
    const { optionsToAdd, optionsOrderToAdd } = localToSalesForceDifference(
      field.editors.dropdownConfig.options,
      sfValuesToMerge,
    );

    const mergedOptions = { ...field.editors.dropdownConfig.options, ...optionsToAdd };
    const mergedOptionsOrder = [
      ...field.editors.dropdownConfig.optionsOrder,
      ...optionsOrderToAdd,
    ];

    actions.push(
      updateFormValue(
        ['fields', field.id, 'editors', 'dropdownConfig', 'options'],
        mergedOptions,
        false,
      ),
    );
    actions.push(
      updateFormValue(
        ['fields', field.id, 'editors', 'dropdownConfig', 'optionsOrder'],
        mergedOptionsOrder,
        false,
      ),
    );
  }

  return actions;
}
